import { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Button } from './Button';

/**
 * Date range preset selector component
 *
 * Provides quick filters for common date ranges:
 * - All time
 * - Today
 * - Last 7 days
 * - Last 30 days
 * - Last 90 days
 * - Last year
 *
 * Features:
 * - Reusable across different features
 * - Customizable translation namespace
 * - Two variants: buttons or select dropdown
 * - Optimized with React.memo
 * - Accessible button group or select
 *
 * @param {Object} props
 * @param {string} props.value - Currently selected preset
 * @param {Function} props.onChange - Callback when preset changes (receives preset string)
 * @param {string} [props.variant='buttons'] - Display variant: 'buttons' or 'select'
 * @param {string} [props.translationNamespace='common'] - i18n namespace for translations
 * @param {string} [props.label] - Optional label for the preset group
 * @param {string} [props.className] - Optional className for the container
 *
 * @example
 * // Buttons variant (default)
 * <DateRangePresets
 *   value={currentPreset}
 *   onChange={(preset) => setPreset(preset)}
 *   translationNamespace="auditLogs.filters"
 * />
 *
 * @example
 * // Select variant
 * <DateRangePresets
 *   value={currentPreset}
 *   onChange={(preset) => setPreset(preset)}
 *   variant="select"
 *   label="Date range"
 * />
 */
export const DateRangePresets = memo(function DateRangePresets({
  value,
  onChange,
  variant = 'buttons',
  translationNamespace = 'common.datePresets',
  label,
  className = '',
}) {
  const { t } = useTranslation();

  const handlePresetClick = useCallback(
    (preset) => {
      onChange(preset);
    },
    [onChange]
  );

  const presets = [
    { value: 'all', labelKey: 'all' },
    { value: 'today', labelKey: 'today' },
    { value: 'last_7_days', labelKey: 'last7Days' },
    { value: 'last_30_days', labelKey: 'last30Days' },
    { value: 'last_90_days', labelKey: 'last90Days' },
    { value: 'last_year', labelKey: 'lastYear' },
  ];

  // Render as select dropdown
  if (variant === 'select') {
    return (
      <div className={className}>
        {label && (
          <label className="block text-xs font-medium text-muted mb-1">
            {label}
          </label>
        )}
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full py-2.5 px-4 border border-border-strong rounded-lg focus:ring-2 focus:ring-brand focus:border-brand transition-colors"
        >
          {presets.map(({ value: presetValue, labelKey }) => (
            <option key={presetValue} value={presetValue}>
              {t(`${translationNamespace}.${labelKey}`)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // Render as buttons (default)
  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-label mb-1.5">
          {label}
        </label>
      )}
      <div className="flex gap-2 flex-wrap">
        {presets.map(({ value: presetValue, labelKey }) => (
          <Button
            key={presetValue}
            variant={value === presetValue ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => handlePresetClick(presetValue)}
          >
            {t(`${translationNamespace}.${labelKey}`)}
          </Button>
        ))}
      </div>
    </div>
  );
});

DateRangePresets.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['buttons', 'select']),
  translationNamespace: PropTypes.string,
  label: PropTypes.string,
  className: PropTypes.string,
};
